/**
 * What each role may reach, menu by menu.
 *
 * The grid describes; the wizard changes. Both read the same tree from the
 * server — which menus exist and which verbs each one actually supports — so
 * neither can offer a Delete on a screen that has no delete, and neither can
 * drift from what the API route guards enforce.
 */
import { useState, useEffect, useCallback } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import { isOffline } from '../../lib/storage.js';
import api from '../../lib/api.js';
import Icon from '../../lib/icons.jsx';
import { EmptyState, Pill } from '../../components/Primitives.jsx';
import AccessWizard from '../../components/modals/AccessWizard.jsx';
import RoleSheet from '../../components/modals/RoleSheet.jsx';
import NewRoleSheet from '../../components/modals/NewRoleSheet.jsx';
import { Section } from './SettingsBits.jsx';

export default function RolesPane() {
  const { toast } = useApp();
  const [tree, setTree] = useState(null);
  const [roles, setRoles] = useState(null);
  const [customised, setCustomised] = useState(false);
  const [failed, setFailed] = useState('');
  /* `wizard` carries the role to open on, so Edit from the detail sheet
     lands on that role rather than at step one. */
  const [wizard, setWizard] = useState(null);
  const [detail, setDetail] = useState(null);
  const [adding, setAdding] = useState(false);

  const offline = isOffline();

  const load = useCallback(async () => {
    if (offline) return;
    try {
      const r = await api.auth.access();
      setTree(r);
      setRoles(r.roles);
      setCustomised(r.customised);
      setFailed('');
    } catch (e) {
      setFailed(e?.message || 'Could not load the permissions');
    }
  }, [offline]);

  useEffect(() => { load(); }, [load]);

  const addRole = async name => {
    await api.auth.addRole(name);
    await load();
    toast(`${name} added`);
    /* Straight into setting it up: a role with no access is not finished. */
    setWizard({ role: name, step: 1 });
  };

  const removeRole = async name => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Remove the ${name} role?

Its permissions go with it.`)) return;
    try {
      await api.auth.deleteRole(name);
      await load();
      toast(`${name} removed`);
    } catch (e) {
      toast(e?.message || 'Could not remove it');
    }
  };

  const save = async next => {
    const r = await api.auth.saveAccess(next);
    setRoles(r.roles);
    setCustomised(r.customised);
    toast('Permissions saved');
  };

  if (offline) {
    return (
      <Section title="Roles &amp; permissions" hint="What each role is allowed to do.">
        <EmptyState icon="lock" tone="g-purple" title="Not available offline">
          Permissions live on the server, which is also what enforces them.
        </EmptyState>
      </Section>
    );
  }

  if (failed) {
    return (
      <Section title="Roles &amp; permissions">
        <EmptyState icon="alert" tone="g-red" title="Could not load the permissions">{failed}</EmptyState>
      </Section>
    );
  }

  if (!tree || !roles) {
    return (
      <Section title="Roles &amp; permissions">
        <div className="loadrow"><span className="spin" />Loading…</div>
      </Section>
    );
  }

  const roleNames = tree.names || Object.keys(tree.defaults);
  const builtIn = tree.builtIn || Object.keys(tree.defaults);
  const menuCount = tree.modules.reduce((n, m) => n + m.menus.length, 0);
  const opened = role => Object.keys(roles[role] || {})
    .filter(k => (roles[role][k] || []).includes('view')).length;

  return (
    <>
      <Section
        title="Roles &amp; permissions"
        hint={
          customised
            ? 'Your workspace has its own policy. Every API route checks it before it touches a record.'
            : 'Running on the built-in defaults. Every API route checks them before it touches a record.'
        }
        action={
          <div className="rowb" style={{ margin: 0 }}>
            <button type="button" className="btn" onClick={() => setAdding(true)}>
              <Icon name="plus" />Add role
            </button>
            <button type="button" className="btn primary" onClick={() => setWizard({ role: null, step: 0 })}>
              <Icon name="lock" />Set permissions
            </button>
          </div>
        }
      >
        <div className="tscroll">
          <table>
            <thead>
              <tr><th>Role</th><th>Menus</th><th>Can create</th><th>Can edit</th><th>Can delete</th><th /></tr>
            </thead>
            <tbody>
              {roleNames.map(role => {
                const p = roles[role] || {};
                const count = v => Object.values(p).filter(list => list.includes(v)).length;
                return (
                  <tr key={role}>
                    <td>
                      <b>{role}</b>
                      {role === 'Owner'
                        ? <div className="dim" style={{ fontSize: 11.5 }}>Always everything</div>
                        : !builtIn.includes(role)
                          ? <div className="dim" style={{ fontSize: 11.5 }}>Added by you</div>
                          : null}
                    </td>
                    <td>
                      <Pill tone={opened(role) === menuCount ? 'p-green' : opened(role) ? 'p-blue' : 'p-grey'}>
                        {opened(role)} of {menuCount}
                      </Pill>
                    </td>
                    <td className="dim">{count('create')}</td>
                    <td className="dim">{count('edit')}</td>
                    <td className="dim">{count('delete')}</td>
                    <td>
                      <div className="acts">
                        <button
                          type="button"
                          title="Show every menu"
                          onClick={() => setDetail(role)}
                        >
                          <Icon name="eye" />
                        </button>
                        {builtIn.includes(role) ? null : (
                          <button
                            type="button"
                            className="rm"
                            title="Remove this role"
                            onClick={() => removeRole(role)}
                          >
                            <Icon name="trash" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

      </Section>

      {detail ? (
        <RoleSheet
          key={detail}
          role={detail}
          tree={tree}
          policy={roles[detail] || {}}
          onClose={() => setDetail(null)}
          onEdit={role => { setDetail(null); setWizard({ role, step: 2 }); }}
        />
      ) : null}

      {adding ? (
        <NewRoleSheet
          existing={roleNames}
          onClose={() => setAdding(false)}
          onSaved={addRole}
        />
      ) : null}

      {wizard ? (
        <AccessWizard
          tree={tree}
          roles={roles}
          startRole={wizard.role}
          startStep={wizard.step}
          onClose={() => setWizard(null)}
          onSave={save}
        />
      ) : null}
    </>
  );
}
